import { useState, useEffect } from "react";
import { 
  Scale, Users, DollarSign, TrendingUp, AlertTriangle, 
  ChevronLeft, Download, Search, ChevronRight, ChevronDown,
  Calendar, RefreshCw, UserCheck, UserX, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend }) => (
  <div className="bg-card border border-border rounded-lg p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </div>
    {trend && (
      <div className="mt-3 pt-3 border-t border-border/50">
        <span className={`text-xs ${trend > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
          {trend > 0 ? '+' : ''}{trend}% from last month
        </span>
      </div>
    )}
  </div>
);

// User Row Component
const UserRow = ({ user, onSelect }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "monthly":
      case "annual":
        return "text-green-500 bg-green-500/10";
      case "survey_pass":
        return "text-blue-500 bg-blue-500/10";
      case "expired":
        return "text-red-500 bg-red-500/10";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <tr 
      className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors"
      onClick={() => onSelect(user)}
    >
      <td className="py-3 px-4 text-sm text-foreground">{user.email}</td>
      <td className="py-3 px-4">
        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(user.subscription_status)}`}>
          {user.subscription_status}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{user.messages_used}</td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(user.created_at)}</td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(user.subscription_expires_at)}</td>
      <td className="py-3 px-4">
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </td>
    </tr>
  );
};

// User Detail Modal
const UserDetailModal = ({ user, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [extendDays, setExtendDays] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");

  if (!user) return null;

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    setLoading(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { subscription_status: selectedPlan });
      toast.success("User subscription updated");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error("Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!extendDays || parseInt(extendDays) <= 0) return;
    setLoading(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { extend_days: parseInt(extendDays) });
      toast.success(`Extended subscription by ${extendDays} days`);
      onUpdate();
      onClose();
    } catch (err) {
      toast.error("Failed to extend subscription");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">User Details</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-foreground font-medium">{user.email}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="text-foreground capitalize">{user.subscription_status}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Messages Used</p>
                <p className="text-foreground">{user.messages_used}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Registered</p>
                <p className="text-foreground text-sm">{formatDate(user.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Expires</p>
                <p className="text-foreground text-sm">{formatDate(user.subscription_expires_at)}</p>
              </div>
            </div>
            {user.subscription_cancelled && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
                Subscription cancelled on {formatDate(user.subscription_cancelled_at)}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-border pt-6 space-y-4">
            <h3 className="text-sm font-medium text-foreground">Admin Actions</h3>
            
            {/* Upgrade Plan */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Change Plan</label>
              <div className="flex gap-2">
                <select 
                  className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  <option value="">Select plan...</option>
                  <option value="monthly">Monthly ($39)</option>
                  <option value="annual">Annual ($299)</option>
                  <option value="survey_pass">Survey Pass ($129)</option>
                </select>
                <Button 
                  onClick={handleUpgrade} 
                  disabled={!selectedPlan || loading}
                  size="sm"
                >
                  Apply
                </Button>
              </div>
            </div>

            {/* Extend Subscription */}
            {user.subscription_status !== "free" && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Extend Subscription</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Days to add"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    className="flex-1"
                    min="1"
                  />
                  <Button 
                    onClick={handleExtend} 
                    disabled={!extendDays || loading}
                    variant="outline"
                    size="sm"
                  >
                    Extend
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = ({ onBack, user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 20;

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/admin/dashboard");
      setDashboard(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        onBack();
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (currentPage * pageSize).toString(),
        limit: pageSize.toString()
      });
      if (statusFilter) params.append("status", statusFilter);
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, statusFilter]);

  const handleExportCSV = async () => {
    try {
      const response = await api.get("/admin/users/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `opscounsel_users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Users exported successfully");
    } catch (err) {
      toast.error("Failed to export users");
    }
  };

  const handleUserSelect = async (user) => {
    try {
      const response = await api.get(`/admin/users/${user.id}`);
      setSelectedUser(response.data.user);
    } catch (err) {
      toast.error("Failed to load user details");
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchDashboard();
    fetchUsers();
  };

  const filteredUsers = searchQuery 
    ? users.filter(u => u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    : users;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Back</span>
            </button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              <span className="font-semibold text-foreground">OpsCounsel™ Admin</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Alerts */}
        {dashboard?.alerts?.email_failures > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <span className="text-sm text-destructive">
              {dashboard.alerts.email_failures} email delivery failures detected
            </span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Users"
            value={dashboard?.users?.total || 0}
            subtitle={`${dashboard?.users?.free || 0} free`}
            icon={Users}
          />
          <StatCard
            title="Paid Subscribers"
            value={dashboard?.users?.paid?.total || 0}
            subtitle={`${dashboard?.users?.paid?.monthly || 0} monthly, ${dashboard?.users?.paid?.annual || 0} annual`}
            icon={UserCheck}
          />
          <StatCard
            title="MRR"
            value={`$${dashboard?.revenue?.mrr?.toLocaleString() || 0}`}
            subtitle="Monthly recurring revenue"
            icon={DollarSign}
          />
          <StatCard
            title="Total Revenue"
            value={`$${dashboard?.revenue?.total?.toLocaleString() || 0}`}
            subtitle="All-time"
            icon={TrendingUp}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Survey Passes</p>
            <p className="text-xl font-bold text-foreground">{dashboard?.users?.paid?.survey_pass || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Paywall Hits</p>
            <p className="text-xl font-bold text-foreground">{dashboard?.funnel?.paywall_hits || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Conversions</p>
            <p className="text-xl font-bold text-foreground">{dashboard?.funnel?.conversions || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Conversion Rate</p>
            <p className="text-xl font-bold text-foreground">{dashboard?.funnel?.conversion_rate || 0}%</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Cancellations</p>
            <p className="text-xl font-bold text-foreground">{dashboard?.churn?.cancellations || 0}</p>
          </div>
        </div>

        {/* Recent Transactions */}
        {dashboard?.revenue?.recent_transactions?.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-5 mb-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Recent Transactions</h3>
            <div className="space-y-3">
              {dashboard.revenue.recent_transactions.slice(0, 5).map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                  <div>
                    <p className="text-sm text-foreground">{tx.email}</p>
                    <p className="text-xs text-muted-foreground">{tx.plan}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">${tx.amount}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(tx.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h3 className="text-sm font-semibold text-foreground">User Management</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <select
                className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
              >
                <option value="">All Status</option>
                <option value="free">Free</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="survey_pass">Survey Pass</option>
                <option value="expired">Expired</option>
              </select>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Messages</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Registered</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Expires</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <UserRow key={user.id} user={user} onSelect={handleUserSelect} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalUsers)} of {totalUsers}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={(currentPage + 1) * pageSize >= totalUsers}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={() => { fetchUsers(); fetchDashboard(); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
